import React from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import LoginPage from "pages/LoginPage";
import { withLoading } from "hocs/withLoading.hoc";
import RequireAuth from "./RequireAuth";
import MainLayout from "components/layouts/MainLayout/MainLayout";
import ScrollToTop from "hooks/scrollTop";

export const DASHBOARD_PATH = "/";

const AuthLayoutFallback = React.lazy(
  () => import("components/layouts/AuthLayout/AuthLayout")
);

const PageNotFoundPage = React.lazy(() => import("pages/404Page"));

const AnalyticsPage = React.lazy(() => import("pages/AnalyticsPage"));
const RolesPage = React.lazy(() => import("pages/RolesPage"));
const PipelineDetailsPage = React.lazy(
  () => import("pages/PipelineDetailsPage")
);
const PipelinePage = React.lazy(() => import("pages/PipelinePage"));
const RoleDetailPage = React.lazy(() => import("pages/RoleDetailsPage"));
const ProfileDetailsPage = React.lazy(() => import("pages/ProfileDetailsPage"));
const SearchPage = React.lazy(() => import("pages/SearchPage"));
const SearchResultPage = React.lazy(() => import("pages/SearchResultPage"));
const SearchByNamePage = React.lazy(() => import("pages/SearchByNamePage"));
const ChatPage = React.lazy(() => import("pages/ChatPage"));
const VegaPage = React.lazy(() => import("pages/VegaPage"));
const IntegrationsPage = React.lazy(() => import("pages/IntegrationPage"));
const IntegrationResponsePage = React.lazy(
  () => import("pages/IntegrationResponsePage")
);
const IntegrationResponseOutlookPage = React.lazy(
  () => import("pages/IntegrationResponseOutlookPage")
);

const RecentSearchesListPage = React.lazy(
  () => import("pages/RecentSearchesListPage")
);
const SavedSearchPage = React.lazy(() => import("pages/SavedSearchPage"));
const Logout = React.lazy(() => import("./Logout"));
const Analytics = withLoading(AnalyticsPage);
const Roles = withLoading(RolesPage);
const Pipeline = withLoading(PipelinePage);
const PipelineDetails = withLoading(PipelineDetailsPage);
const CandidateProfile = withLoading(ProfileDetailsPage);
const Search = withLoading(SearchPage);
const SearchResult = withLoading(SearchResultPage);
const RoleDetails = withLoading(RoleDetailPage);
const RecentSearchesList = withLoading(RecentSearchesListPage);
const SavedSearch = withLoading(SavedSearchPage);
const SearchByName = withLoading(SearchByNamePage);
const Integrations = withLoading(IntegrationsPage);
const IntegrationResponse = withLoading(IntegrationResponsePage);
const IntegrationResponseOutlook = withLoading(IntegrationResponseOutlookPage);
const Chat = withLoading(ChatPage);
const Vega = withLoading(VegaPage);

const PageNotFound = withLoading(PageNotFoundPage);
const LogoutFallback = withLoading(Logout);

export const AppRouter: React.FC = () => {
  const protectedLayout = (
    <RequireAuth>
      <MainLayout />
    </RequireAuth>
  );

  return (
    <BrowserRouter>
      <ScrollToTop />
      <Routes>
        <Route path={DASHBOARD_PATH} element={protectedLayout}>
          <Route index element={<Roles />} />
          <Route path="/analytics" element={<Analytics />}></Route>
          <Route path="/chat" element={<Chat />}></Route>
          <Route path="/vega" element={<Vega />}></Route>
          <Route path="/roles">
            <Route path="list" element={<Roles />} />
            <Route path="role-details/:id" element={<RoleDetails />} />
          </Route>
          <Route path="/pipeline">
            <Route path="list" element={<Pipeline />} />
            <Route path="pipeline-details/:id" element={<PipelineDetails />} />
          </Route>
          <Route path="/candidate">
            <Route path=":id" element={<CandidateProfile />} />
          </Route>
          <Route path="/integrations">
            <Route path="list" element={<Integrations />} />
            <Route
              path="integration-response"
              element={<IntegrationResponse />}
            />
            <Route
              path="integration-response-outlook"
              element={<IntegrationResponseOutlook />}
            />
          </Route>

          <Route path="/search">
            <Route path="initiate-search" element={<Search />} />
            <Route path="recent-search-log" element={<RecentSearchesList />} />
            <Route path="saved-search" element={<SavedSearch />} />
            <Route path="search-by-name" element={<SearchByName />} />

            <Route path="initiate-role-search/:roleId" element={<Search />} />
            <Route
              path="edit-search/:searchId/:graphSearchId"
              element={<Search />}
            />
            <Route
              path="search-result/:id/:searchId"
              element={<SearchResult />}
            />
            <Route path="search-result/:id" element={<SearchResult />} />
          </Route>
        </Route>

        <Route path="/auth" element={<AuthLayoutFallback />}>
          <Route path="login" element={<LoginPage />} />
        </Route>
        <Route path="/logout" element={<LogoutFallback />} />
        <Route path="/404" element={<PageNotFound />} />
      </Routes>
    </BrowserRouter>
  );
};
